import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  usePreviewReminderEmailMutation,
  type PreviewReminderEmailResponse,
} from '@proaxiv/server-api'
import type { AppEvent } from '../../store/eventsSlice'

/** Props for {@link ReminderEmailView}. */
export interface ReminderEmailViewProps {
  event: AppEvent
  /** Sample recipient's display name, e.g. "Jane Doe". */
  participantName: string
  /** Sample recipient's email address, shown in the "To" line. */
  participantEmail: string
}

/**
 * Disables every link in a rendered reminder-email document so the preview is inert.
 *
 * <p>The template's "View My Event Page" button and any other links (e.g. a footer unsubscribe
 * link) are real {@code <a href>} tags pointing at the fake {@code "?tk=preview"} magic link —
 * the iframe's sandbox attribute only blocks top-level navigation, not the frame navigating
 * itself, so without this a click would replace the preview with a broken/unrelated page.
 * Disabling them keeps the preview visually identical to the real email while making it inert,
 * matching how other preview-mode CTAs in the app (e.g. "Open in Google Maps") look real but do
 * nothing.
 *
 * @param doc the iframe's content document, once its content has loaded
 */
// eslint-disable-next-line react-refresh/only-export-components
export function disablePreviewLinks(doc: Document): void {
  doc.querySelectorAll('a[href]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => e.preventDefault())
    anchor.setAttribute('aria-disabled', 'true')
    ;(anchor as HTMLElement).style.cursor = 'default'
  })
}

/**
 * Read-only preview of the automatic participation-reminder email (see
 * `ep_field_auto_reminder`) that accepted participants receive when the event's "Automatic
 * reminder date & time" arrives.
 *
 * <p>Unlike {@link PublicEventView} / {@link TicketView}, this has no on-screen counterpart to
 * extract from — reminder emails only ever exist as server-rendered HTML. Rather than reproduce
 * that copy/layout in React (which would silently drift from the real email over time), this
 * calls the engine's {@code POST /api/v1/events/reminder-preview} endpoint, which renders the
 * exact same template used for real reminder sends, and displays the resulting HTML in a
 * sandboxed iframe — a true WYSIWYG preview that can never visually diverge from production.
 */
export function ReminderEmailView({
  event,
  participantName,
  participantEmail,
}: ReminderEmailViewProps) {
  const { t, i18n } = useTranslation()
  const hasReminderDate = Boolean(event.autoReminderAt)
  const [previewReminderEmail, { data, isLoading, isError }] = usePreviewReminderEmailMutation()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeHeight, setIframeHeight] = useState(600)

  useEffect(() => {
    previewReminderEmail({
      reminderPreviewBody: {
        title: event.title,
        startDate: event.startDateTime,
        venue: event.address,
        timezone: event.timezone,
        primaryColor: event.branding.primary,
        backgroundColor: event.branding.background,
        participantName,
        previewAsOf: event.autoReminderAt,
        language: i18n.language,
      },
    })
  }, [
    previewReminderEmail,
    event.title,
    event.startDateTime,
    event.address,
    event.timezone,
    event.branding.primary,
    event.branding.background,
    event.autoReminderAt,
    participantName,
    i18n.language,
  ])

  const html = (data as PreviewReminderEmailResponse | undefined)?.html ?? ''

  // Resize the iframe to fit its rendered content exactly, so the email preview never scrolls
  // on its own — the shared desktop scale-to-fit logic (see EventEditorPage) then shrinks this
  // whole pane, iframe included, down to the viewport just like the other preview tabs.
  const handleLoad = () => {
    const doc = iframeRef.current?.contentDocument
    if (doc?.documentElement) {
      setIframeHeight(doc.documentElement.scrollHeight)
    }
    if (doc) {
      disablePreviewLinks(doc)
    }
  }

  return (
    <div className="bg-pax-light-purple/30 flex min-h-screen flex-col gap-4 px-6 py-8">
      {!hasReminderDate && (
        <p className="text-pax-black rounded-xl bg-white px-4 py-3 text-sm shadow-sm">
          {t('ep_reminder_preview_unset_notice')}
        </p>
      )}

      <div className="border-pax-purple/25 mx-auto flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border bg-white shadow-lg">
        {/* Envelope header — SMTP "To"/"Subject" headers aren't part of the rendered email body,
            so this chrome stays organiser-facing UI (translated via i18n) around the iframe. */}
        <div className="border-pax-purple/15 bg-pax-light-purple/40 flex flex-col gap-1 border-b px-5 py-3 text-xs">
          <p className="text-pax-gray">
            <span className="font-semibold">{t('ep_reminder_email_to_label')}:</span>{' '}
            {participantName} &lt;{participantEmail}&gt;
          </p>
          <p className="text-pax-black">
            <span className="font-semibold">{t('ep_reminder_email_subject_label')}:</span>{' '}
            {t('ep_reminder_email_subject', { title: event.title })}
          </p>
        </div>

        {isLoading && !html && (
          <p className="text-pax-gray px-6 py-10 text-center text-sm">
            {t('ep_reminder_preview_loading')}
          </p>
        )}
        {isError && !html && (
          <p className="text-pax-red px-6 py-10 text-center text-sm">
            {t('ep_reminder_preview_error')}
          </p>
        )}
        {html && (
          <iframe
            ref={iframeRef}
            title={t('ep_preview_tab_reminder')}
            srcDoc={html}
            onLoad={handleLoad}
            // No script execution regardless of content; allow-same-origin only so the parent
            // can measure the rendered height above (see handleLoad).
            sandbox="allow-same-origin"
            className="w-full border-0"
            style={{ height: iframeHeight }}
          />
        )}
      </div>
    </div>
  )
}
